import { createTheme } from '@mui/material/styles';

/**
 * Creates the MUI theme for the Policy Tracker UI.
 *
 * Configures the brand teal palette and switches between light and dark mode
 * based on the `isDark` flag supplied by the {@link useTheme} hook.
 *
 * @param isDark - Whether dark mode is currently active.
 * @returns A configured MUI `Theme` instance.
 */
export function createAppTheme(isDark: boolean) {
    return createTheme({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: { main: '#0d9488', dark: '#0f766e', light: '#14b8a6' },
            error: { main: '#dc2626' },
            warning: { main: '#d97706' },
            success: { main: '#16a34a' },
            info: { main: '#0d9488' },
        },
        components: {
            // #2857/#2863 — restore dotted palette color-prop support on
            // Typography. MUI v6+ removed system-prop handling from
            // Typography's `color` prop: only camelCase palette keys
            // (`textSecondary`, `primary`, `error`, …) match the variants the
            // component generates, so the long-standing dotted forms
            // (`color="text.secondary"`, `color="success.main"`) — used ~100
            // places in this codebase and all over MUI's own v5 docs — became
            // silent no-ops. The element then inherits the surrounding body
            // color (text.primary), which is how the auditor footer's "muted"
            // caption shipped near-black (#2857) and the SignByAuth success
            // heading lost its green (#2863). (`Link` is unaffected — for
            // non-enum color strings it falls back to `sx`, which still
            // resolves dotted theme paths.) This override walks any dotted
            // color prop through the active palette (`text.secondary`,
            // `success.main`, `grey.500`, …), so light and dark mode both get
            // the intended tone. Dot-free enum values and paths that don't
            // land on a color string return no style and fall through to
            // MUI's default handling.
            MuiTypography: {
                styleOverrides: {
                    root: ({ theme, ownerState }) => {
                        const { color } = ownerState;
                        if (typeof color !== 'string' || !color.includes('.')) {
                            return {};
                        }
                        const resolved = color.split('.').reduce<unknown>(
                            (node, segment) =>
                                node !== null && typeof node === 'object'
                                    ? (node as Record<string, unknown>)[segment]
                                    : undefined,
                            theme.palette,
                        );
                        return typeof resolved === 'string' ? { color: resolved } : {};
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                    },
                },
            },
            // #2207 — MUI's default standard-variant Alert palette uses a
            // low-alpha tint that disappears against a dark page surface;
            // body text drops below WCAG AA contrast and only the
            // severity icon hints there's an alert at all. Per-severity
            // override via the `root` slot + ownerState dispatch:
            //   (a) every Alert gets a 4px left border in the severity
            //       color (redundant accent stripe even when the surface
            //       washes out),
            //   (b) dark mode bumps the surface to a saturated tint of
            //       the severity color so the alert reads as a colored
            //       region, not an extra grey row,
            //   (c) dark mode forces body text to an off-white so WCAG
            //       AA contrast is met against the new tinted surface.
            // Light mode keeps MUI's defaults and just gets the border
            // for visual parity.
            MuiAlert: {
                styleOverrides: {
                    root: ({ theme, ownerState }) => {
                        const sev: 'warning' | 'error' | 'info' | 'success' =
                            ownerState.severity ?? 'info';
                        const color = {
                            warning: '#d97706',
                            error: '#dc2626',
                            info: '#0d9488',
                            success: '#16a34a',
                        }[sev];
                        const darkBg = {
                            warning: 'rgba(217, 119, 6, 0.22)',
                            error: 'rgba(220, 38, 38, 0.22)',
                            info: 'rgba(13, 148, 136, 0.22)',
                            success: 'rgba(22, 163, 74, 0.22)',
                        }[sev];
                        return {
                            borderLeftWidth: 4,
                            borderLeftStyle: 'solid',
                            borderLeftColor: color,
                            ...(theme.palette.mode === 'dark' && {
                                backgroundColor: darkBg,
                                color: '#f1f5f9',
                                '& .MuiAlert-message': { color: '#f1f5f9' },
                            }),
                        };
                    },
                },
            },
        },
    });
}
